import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import axios from "axios";
import moment from "moment";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";

import DataTable from "../../components/datatable/DataTable";
import SearchInput from "../../components/searchinput/SearchInput";
import Pagination from "../../components/pagination/Pagination";
import Styles from "../lets_talk/Letstalk.module.css";
import { AxiosInstance } from "../../axios";
import { logDOM } from "@testing-library/dom";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(customParseFormat);

const Candidates_List = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const [allFilteredCandidates, setAllFilteredCandidates] = useState([]);
  const [totalFilteredItems, setTotalFilteredItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasRestoredFilters, setHasRestoredFilters] = useState(false);

  // Department options
  const [departmentOptions, setDepartmentOptions] = useState([]);

  // Department data
  const departmentData = [
    { id: 1, name: "Software Development" },
    { id: 2, name: "Quality Assurance" },
    { id: 3, name: "Cloud Engineering" },
    { id: 4, name: "IT Project Management" },
    { id: 5, name: "Business Administration" },
    { id: 6, name: "Accounts" },
    { id: 7, name: "Human Resource Development" },
    { id: 8, name: "Product Design" },
  ];

  // Initialize filters from URL params
  const getInitialFilters = () => ({
    search: searchParams.get("search") || "",
    status: searchParams.get("status") || "",
    isRelocated: searchParams.get("isRelocated") || "",
    departmentId: searchParams.get("department") || "",
    fromDate: searchParams.get("fromDate") || "",
    toDate: searchParams.get("toDate") || "",
  });

  const initialCurrentPage = Number(searchParams.get("page")) || 1;
  const initialRowsPerPage = Number(searchParams.get("limit")) || 10;

  // Main filter state
  const [filters, setFilters] = useState(getInitialFilters());

  // Temporary states for filter popups
  const [tempFilters, setTempFilters] = useState({
    status: "",
    isRelocated: "",
    departmentId: "",
    fromDate: "",
    toDate: "",
    dateLabel: "",
  });

  const [chipData, setChipData] = useState([]);
  const [isOpen, setIsOpen] = useState({ status: false, column: "" });
  const filterRef = useRef(null);

  const [currentPage, setCurrentPage] = useState(initialCurrentPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
  const rowsPerPageOptions = [10, 15, 20, 50];

  // Initialize department options
  useEffect(() => {
    const mappedDepartments = departmentData.map((dept) => ({
      label: dept.name,
      value: String(dept.id),
    }));
    setDepartmentOptions(mappedDepartments);
  }, []);

  // ONLY effect to restore filters from location state - runs once
  useEffect(() => {
    if (location.state?.candidateFilters && !hasRestoredFilters) {
      setFilters(location.state.candidateFilters);
      setHasRestoredFilters(true);
    }
  }, [location.state?.candidateFilters, hasRestoredFilters]);

  // SINGLE API CALL EFFECT - triggers only when filters change
// SINGLE API CALL EFFECT - triggers only when filters change
  useEffect(() => {
    const abortController = new AbortController();
    setLoading(true);
    try {
      const payload = {
        search: filters.search || null,
        status: filters.status === "" ? null : Number(filters.status),
        is_relocated:
          filters.isRelocated === "" ? null : Number(filters.isRelocated),
        department_id:
          filters.departmentId === "" ? null : Number(filters.departmentId),
        start_date: filters.fromDate
          ? dayjs(filters.fromDate, "DD/MM/YYYY").format("YYYY-MM-DD 00:00:00")
          : null,
        end_date: filters.toDate
          ? dayjs(filters.toDate, "DD/MM/YYYY").format("YYYY-MM-DD 23:59:59")
          : null,
        job_id: null,
      };

      AxiosInstance.post("/api/joinourteam/candidate/list", payload, {
        signal: abortController.signal,
      })
        .then((response) => {
          if (!abortController.signal.aborted) {
            if (response.data && response.data.status) {
              setAllFilteredCandidates(response.data.Candidates || []);
              setTotalFilteredItems(response.data.Candidates?.length || 0);
              setCurrentPage(1);
            } else {
              console.error(
                "API response status is false:",
                response.data.message
              );
              setAllFilteredCandidates([]);
              setTotalFilteredItems(0);
            }
          }
        })
        .catch((error) => {
          if (!abortController.signal.aborted) {
            console.error("Error fetching candidate list:", error);
            setAllFilteredCandidates([]);
            setTotalFilteredItems(0);
          }
        })
        .finally(() => {
          if (!abortController.signal.aborted) {
            setLoading(false);
          }
        });
    } catch (error) {
      console.error("Error in payload preparation:", error);
      setLoading(false);
    }

    return () => {
      abortController.abort();
    };
  }, [filters]);

  const filterOptions = useMemo(
    () => ({
      status: [
        { label: "Pending", value: "0" },
        { label: "Reviewing", value: "1" },
        { label: "Shortlisted", value: "2" },
        { label: "Scheduled", value: "3" },
        { label: "On Hold", value: "4" },
        { label: "Offered", value: "5" },
        { label: "Rejected", value: "6" },
      ],
      is_relocated: [
        { label: "Yes", value: "1" },
        { label: "No", value: "0" },
      ],
      Department: departmentOptions,
    }),
    [departmentOptions]
  );

  const getStatusLabel = useCallback((statusValue) => {
    switch (statusValue) {
      case 0:
        return "Pending";
      case 1:
        return "Reviewing";
      case 2:
        return "Shortlisted";
      case 3:
        return "Scheduled";
      case 4:
        return "On Hold";
      case 5:
        return "Offered";
      case 6:
        return "Rejected";
      default:
        return "-";
    }
  }, []);

  // Effect to update URL search parameters
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.search) params.set("search", filters.search);
    if (filters.status) params.set("status", filters.status);
    if (filters.isRelocated) params.set("isRelocated", filters.isRelocated);
    if (filters.departmentId) params.set("department", filters.departmentId);
    if (filters.fromDate) params.set("fromDate", filters.fromDate);
    if (filters.toDate) params.set("toDate", filters.toDate);
    if (currentPage !== 1) params.set("page", currentPage.toString());
    if (rowsPerPage !== 10) params.set("limit", rowsPerPage.toString());

    setSearchParams(params);
  }, [filters, currentPage, rowsPerPage, setSearchParams]);

  // Effect to generate and update filter chips
  useEffect(() => {
    const currentChips = [];
    if (filters.search) {
      currentChips.push({
        type: "search",
        value: filters.search,
        label: `Search: "${filters.search}"`,
      });
    }
    if (filters.status) {
      const statusLabel =
        filterOptions.status.find((opt) => opt.value === filters.status)
          ?.label || filters.status;
      currentChips.push({
        label: `Status: ${statusLabel}`,
        type: "status",
        value: filters.status,
      });
    }
    if (filters.isRelocated) {
      const relocatedLabel =
        filterOptions.is_relocated.find(
          (opt) => opt.value === filters.isRelocated
        )?.label || filters.isRelocated;
      currentChips.push({
        label: `Can Relocate: ${relocatedLabel}`,
        type: "is_relocated",
        value: filters.isRelocated,
      });
    }
    if (filters.departmentId) {
      const departmentName =
        departmentOptions.find((opt) => opt.value === filters.departmentId)
          ?.label || filters.departmentId;
      currentChips.push({
        label: `Department: ${departmentName}`,
        type: "department",
        value: filters.departmentId,
      });
    }
    if (filters.fromDate || filters.toDate) {
      let label = "";
      if (filters.fromDate && filters.toDate) {
        label = `Applied Date: ${filters.fromDate} - ${filters.toDate}`;
      } else if (filters.fromDate) {
        label = `From Date: ${filters.fromDate}`;
      } else if (filters.toDate) {
        label = `To Date: ${filters.toDate}`;
      }
      currentChips.push({
        label: label,
        type: "applied_date",
        value: `${filters.fromDate}-${filters.toDate}`,
      });
    }
    setChipData(currentChips);
  }, [filters, filterOptions, departmentOptions]);

  const columns = useMemo(
    () => [
      { header: "S.No", accessor: "sno" },
      { header: "Name", accessor: "full_name", sortable: true },
      { header: "Email Address", accessor: "email", sortable: true },
      { header: "Phone Number", accessor: "phone_number", sortable: true },
      {
        header: "Total Experience",
        accessor: "yearof_experience",
        sortable: true,
      },
      {
        header: "Job ID",
        accessor: "job_id",
      },
      {
        header: "Role",
        accessor: "Role",
      },
      {
        header: "Department",
        accessor: "Department",
        sortable: true,
        filterable: true,
      },
      {
        header: "Applied Date",
        accessor: "applied_date",
        sortable: true,
        filterable: true,
        isDateColumn: true,
      },
      {
        header: "Status",
        accessor: "status",
        sortable: true,
        filterable: true,
      },
    ],
    []
  );

  // Handle search input
  const handleSearch = useCallback((value) => {
    setFilters((prev) => ({ ...prev, search: value }));
  }, []);

  // Handle deletion of individual filter chips
  const handleDeleteChip = useCallback((chipToDelete) => {
    setFilters((prev) => {
      const updated = { ...prev };
      if (chipToDelete.type === "search") {
        updated.search = "";
      } else if (chipToDelete.type === "status") {
        updated.status = "";
      } else if (chipToDelete.type === "is_relocated") {
        updated.isRelocated = "";
      } else if (chipToDelete.type === "department") {
        updated.departmentId = "";
      } else if (chipToDelete.type === "applied_date") {
        updated.fromDate = "";
        updated.toDate = "";
      }
      return updated;
    });

    setTempFilters({
      status: "",
      isRelocated: "",
      departmentId: "",
      fromDate: "",
      toDate: "",
      dateLabel: "",
    });
  }, []);

  // Handle clearing all filters
  const handleAllClear = useCallback(() => {
    setFilters({
      search: "",
      status: "",
      isRelocated: "",
      departmentId: "",
      fromDate: "",
      toDate: "",
    });

    setTempFilters({
      status: "",
      isRelocated: "",
      departmentId: "",
      fromDate: "",
      toDate: "",
      dateLabel: "",
    });
  }, []);

  // Handle opening filter popup
  const handleFilterClick = useCallback(
    (columnAccessor) => {
      setIsOpen({ status: true, column: columnAccessor });

      if (filterOptions.hasOwnProperty(columnAccessor)) {
        const filterMap = {
          status: "status",
          is_relocated: "isRelocated",
          Department: "departmentId",
        };
        const filterKey = filterMap[columnAccessor];
        setTempFilters((prev) => ({
          ...prev,
          [filterKey]: filters[filterKey],
        }));
      } else if (columnAccessor === "applied_date") {
        setTempFilters((prev) => ({
          ...prev,
          fromDate: filters.fromDate,
          toDate: filters.toDate,
        }));
        let label = "";
        if (filters.fromDate && filters.toDate) {
          label = `Applied Date: ${filters.fromDate} - ${filters.toDate}`;
        } else if (filters.fromDate) {
          label = `From Date: ${filters.fromDate}`;
        } else if (filters.toDate) {
          label = `To Date: ${filters.toDate}`;
        } else {
          label = "Date Range";
        }
        setTempFilters((prev) => ({ ...prev, dateLabel: label }));
      }
    },
    [filters, filterOptions]
  );

  // Handle categorical filter change
  const handleCategoricalFilterChange = useCallback((columnAccessor, value) => {
    const filterMap = {
      status: "status",
      is_relocated: "isRelocated",
      Department: "departmentId",
    };
    const filterKey = filterMap[columnAccessor];
    setTempFilters((prev) => ({
      ...prev,
      [filterKey]: value,
    }));
  }, []);

  // Handle applying filter
  const handleApplyFilter = useCallback(() => {
    
    setFilters((prev) => {
      const updated = { ...prev };
      if (isOpen.column === "status") {
        updated.status = tempFilters.status;
      } else if (isOpen.column === "is_relocated") {
        updated.isRelocated = tempFilters.isRelocated;
      } else if (isOpen.column === "Department") {
        updated.departmentId = tempFilters.departmentId;
      }
      return updated;
    });
    setIsOpen({ status: false, column: "" });
  }, [isOpen.column, tempFilters]);

  // Handle clearing specific filter
  const handleClearSpecificFilter = useCallback((columnAccessor) => {
    setFilters((prev) => {
      const updated = { ...prev };
      if (columnAccessor === "status") {
        updated.status = "";
      } else if (columnAccessor === "is_relocated") {
        updated.isRelocated = "";
      } else if (columnAccessor === "Department") {
        updated.departmentId = "";
      } else if (columnAccessor === "applied_date") {
        updated.fromDate = "";
        updated.toDate = "";
      }
      return updated;
    });

    setTempFilters({
      status: "",
      isRelocated: "",
      departmentId: "",
      fromDate: "",
      toDate: "",
      dateLabel: "",
    });

    setIsOpen({ status: false, column: "" });
  }, []);

  // Handle date range apply
  const handleDateRangeApply = useCallback(
    ({ fromDate: selectedFrom, toDate: selectedTo, label: selectedLabel }) => {
      setFilters((prev) => ({
        ...prev,
        fromDate: selectedFrom,
        toDate: selectedTo,
      }));

      setTempFilters((prev) => ({
        ...prev,
        fromDate: selectedFrom,
        toDate: selectedTo,
        dateLabel: selectedLabel,
      }));

      setIsOpen({ status: false, column: "" });
      setCurrentPage(1);
    },
    []
  );

  // Handle clicks outside filter popup
  const handleClickOutside = useCallback(
    (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setIsOpen((prev) => {
          if (prev.status) {
            setTempFilters({
              status: filters.status,
              isRelocated: filters.isRelocated,
              departmentId: filters.departmentId,
              fromDate: filters.fromDate,
              toDate: filters.toDate,
              dateLabel: "",
            });
          }
          return { ...prev, status: false };
        });
      }
    },
    [filters]
  );

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  const processedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;

    const paginatedData = Array.isArray(allFilteredCandidates)
      ? allFilteredCandidates.slice(startIndex, endIndex)
      : [];

    return paginatedData
      .map((user, index) => {
        if (!user) {
          console.warn(
            "Skipping undefined/null user entry during processing:",
            user
          );
          return null;
        }

        const departmentNameForDisplay =
          departmentOptions.find(
            (opt) => String(opt.value) === String(user.department_id)
          )?.label ||
          user.Department ||
          "-";

        return {
          originalData: user,
          sno: startIndex + index + 1,
          full_name: user.full_name || "-",
          email: user.email || "-",
          phone_number: user.phone_number || "-",
          country_name: user.country_name || "-",
          job_id: user.job_id || "-",
          Role: user.role || "-",
          Department: departmentNameForDisplay,
          state_name: user.state_name || "-",
          city_name: user.city_name || "-",
          expected_salary: user.expected_salary || "-",
          yearof_experience: user.yearof_experience || "-",
          status: getStatusLabel(user.status),
          is_relocated: user.is_relocated === 1 ? "Yes" : "No",
          applied_date: user.applied_date
            ? moment(user.applied_date).format("DD/MM/YYYY")
            : "-",
        };
      })
      .filter(Boolean);
  }, [
    allFilteredCandidates,
    currentPage,
    rowsPerPage,
    getStatusLabel,
    departmentOptions,
  ]);

  const handleViewClick = useCallback(
    (rowData) => {
      const { email, job_id } = rowData.originalData;
      navigate("/admin/candidates-view", {
        state: {
          email,
          job_id,
          candidateFilters: filters,
        },
      });
    },
    [navigate, filters]
  );

  const actionIconProps = useMemo(
    () => ({
      onClick: handleViewClick,
    }),
    [handleViewClick]
  );

  const currentAppliedFilters = useMemo(() => {
    const appliedFilters = {};
    if (filters.status) appliedFilters.status = filters.status;
    if (filters.isRelocated) appliedFilters.is_relocated = filters.isRelocated;
    if (filters.departmentId) appliedFilters.Department = filters.departmentId;
    if (filters.fromDate) appliedFilters.fromDate = filters.fromDate;
    if (filters.toDate) appliedFilters.toDate = filters.toDate;
    return appliedFilters;
  }, [filters]);

  return (
    <div className={`container-fluid px-3 ${Styles.main_Container}`}>
      <div className="row mb-3">
        <div className="col-12 col-md-6">
          <SearchInput
            handleKeyDown={handleSearch}
            placeholder="Search with Name, Email, Phone..."
            value={filters.search}
          />
        </div>
      </div>

      <div className="mb-3">
        {chipData.map((chip, index) => (
          <span key={index} className="custom-badge me-2">
            {chip.label}
            <button
              onClick={() => handleDeleteChip(chip)}
              className="btn-close ms-2"
            ></button>
          </span>
        ))}
        {(chipData.length > 0 || filters.search) && (
          <button
            className="btn btn-sm btn-outline-danger ms-2"
            onClick={handleAllClear}
          >
            Clear All Filters
          </button>
        )}
      </div>

      <>
        <DataTable
          loading={loading}
          columns={columns}
          data={processedUsers}
          filterOptions={filterOptions}
          filters={currentAppliedFilters}
          actionIcon={actionIconProps}
          handleFilter={handleFilterClick}
          isOpens={isOpen}
          filterPopupRef={filterRef}
          tempCategoricalFilters={{
            status: tempFilters.status,
            is_relocated: tempFilters.isRelocated,
            Department: tempFilters.departmentId,
          }}
          onCategoricalFilterChange={handleCategoricalFilterChange}
          handleApplyFilter={handleApplyFilter}
          onClearSpecificFilter={handleClearSpecificFilter}
          currentDateRange={{
            fromDate: tempFilters.fromDate,
            toDate: tempFilters.toDate,
            label: tempFilters.dateLabel,
          }}
          onDateRangeApply={handleDateRangeApply}
          ellipsisIndex={[2, 5]}
        />

        <div
          className="mb-3"
          style={{ display: "flex", justifyContent: "flex-end" }}
        >
          <Pagination
            currentPage={currentPage}
            rowsPerPage={rowsPerPage}
            totalItems={totalFilteredItems}
            rowsPerPageOptions={rowsPerPageOptions}
            onPageChange={setCurrentPage}
            onRowsPerPageChange={(value) => {
              setRowsPerPage(value);
              setCurrentPage(1);
            }}
          />
        </div>
      </>
    </div>
  );
};

export default Candidates_List;